import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  AccountCredentialIntegrationType,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

/**
 * Property Meld Integration API Client
 * Handles all Property Meld-specific API operations
 */
export class PropertyMeldClient extends BaseApiClient {
  /**
   * Create account credentials for Property Meld
   */
  async createCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.PROPERTY_MELD },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'POST',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error creating Property Meld account credentials',
      },
    )
  }

  /**
   * [ADMIN] Create account credentials for Property Meld for a specific company
   */
  async createAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.CREATE,
          { integrationType: AccountCredentialIntegrationType.PROPERTY_MELD },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error creating admin Property Meld account credentials',
      },
    )
  }

  /**
   * Update account credentials for Property Meld
   */
  async updateCredentials(
    accountIdentifier: string,
    password: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.PROPERTY_MELD },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ accountIdentifier, password, metadata }),
        })
      },
      {
        errorContext: 'Error updating Property Meld account credentials',
      },
    )
  }

  /**
   * [ADMIN] Update account credentials for Property Meld for a specific company
   */
  async updateAdminCredentials(
    companyId: string,
    accountIdentifier: string,
    password: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.UPDATE,
          { integrationType: AccountCredentialIntegrationType.PROPERTY_MELD },
        )
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ accountIdentifier, password, metadata }),
          },
        )
      },
      {
        errorContext: 'Error updating admin Property Meld account credentials',
      },
    )
  }

  /**
   * Disconnect Property Meld integration
   */
  async disconnect(): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
          { integrationType: AccountCredentialIntegrationType.PROPERTY_MELD },
        )
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting Property Meld integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect Property Meld integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.ACCOUNT_CREDENTIALS.DELETE,
      { integrationType: AccountCredentialIntegrationType.PROPERTY_MELD },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin Property Meld integration',
      },
    )
  }
}

// Export singleton instance
export const propertyMeldClient = new PropertyMeldClient()
